import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { verbaliApi } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { FileSignature, ExternalLink } from 'lucide-react'

interface VerbaleInfo {
  pendente: boolean
  assegnazione_id?: number
  token?: string
  link_firma?: string
  data_assegnazione?: string
  beni_count?: number
  data_invio?: string
}

// Chiave sessionStorage per non riaprire il popup nella stessa sessione per lo stesso verbale
function sessionKey(id: number) {
  return `verbale_popup_shown_${id}`
}

export default function VerbalePopup() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [visible, setVisible] = useState(false)
  const [info, setInfo] = useState<VerbaleInfo | null>(null)

  const isTecnico = ['tecnico', 'tecnico_senior'].includes(user?.role ?? '')

  const { data } = useQuery({
    queryKey: ['mio-verbale-pendente', user?.id],
    queryFn: () => verbaliApi.mioVerbalePendente().then(r => r.data as VerbaleInfo),
    enabled: isTecnico,
    staleTime: 60_000,
    refetchInterval: 300_000,
  })

  useEffect(() => {
    if (!data?.pendente || !data.assegnazione_id) return
    const key = sessionKey(data.assegnazione_id)
    if (sessionStorage.getItem(key)) return
    setInfo(data)
    setVisible(true)
  }, [data])

  const firma = () => {
    if (info?.assegnazione_id) {
      sessionStorage.setItem(sessionKey(info.assegnazione_id), '1')
    }
    setVisible(false)
    if (info?.token) {
      navigate(`/firma-verbale/${info.token}`)
    }
  }

  if (!visible || !info) return null

  const fmtData = (d?: string) => {
    if (!d) return ''
    try { return new Date(d).toLocaleDateString('it-IT') } catch { return d }
  }

  return (
    <>
      {/* Overlay — non chiudibile */}
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 9998,
          background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)',
        }}
      />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed', zIndex: 9999,
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          background: '#fff', borderRadius: 16,
          boxShadow: '0 8px 40px rgba(0,0,0,0.22)',
          maxWidth: 440, width: 'calc(100vw - 32px)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          background: '#1E40AF',
          padding: '18px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              background: 'rgba(255,255,255,0.18)',
              borderRadius: 8, padding: 6,
              display: 'flex', alignItems: 'center',
            }}>
              <FileSignature size={18} color="#fff" />
            </div>
            <div>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>
                Firma richiesta
              </div>
              <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11 }}>
                Verbale di consegna beni
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 22px' }}>
          <div style={{
            background: '#fefce8', border: '1px solid #fbbf24',
            borderRadius: 10, padding: '14px 16px', marginBottom: 18,
          }}>
            <p style={{ margin: 0, fontSize: 14, color: '#92400e', lineHeight: 1.6, fontWeight: 600 }}>
              Hai un verbale di assegnazione beni in attesa della tua firma digitale.
            </p>
          </div>

          <div style={{
            background: '#f8fafc', border: '1px solid #e5e7eb',
            borderRadius: 8, padding: '12px 14px', marginBottom: 20,
            display: 'flex', flexDirection: 'column', gap: 4,
          }}>
            {info.data_assegnazione && (
              <div style={{ fontSize: 13, color: '#374151' }}>
                <span style={{ color: '#6b7280' }}>Data assegnazione:</span>{' '}
                <strong>{fmtData(info.data_assegnazione)}</strong>
              </div>
            )}
            {info.beni_count !== undefined && (
              <div style={{ fontSize: 13, color: '#374151' }}>
                <span style={{ color: '#6b7280' }}>Beni inclusi:</span>{' '}
                <strong>{info.beni_count}</strong>
              </div>
            )}
            {info.data_invio && (
              <div style={{ fontSize: 13, color: '#374151' }}>
                <span style={{ color: '#6b7280' }}>Inviato il:</span>{' '}
                <strong>{fmtData(info.data_invio)}</strong>
              </div>
            )}
          </div>

          <button
            onClick={firma}
            style={{
              width: '100%', padding: '13px 0', borderRadius: 8,
              border: 'none', background: '#1E40AF', color: '#fff',
              fontSize: 14, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <ExternalLink size={15} />
            Firma ora
          </button>
        </div>
      </div>
    </>
  )
}
